import './MapButtons.less';
import MapButton from '../../Atoms/MapButton/MapButton';
import { useAppDispatch, useAppSelector } from '../../../store/hooks/hooks';
import { ReactComponent as Layers } from '../../../res/svg/layers.svg';
import { ReactComponent as AddPoint } from '../../../res/svg/add-point.svg';
import { ReactComponent as AOIIcon } from '../../../res/svg/aoi.svg';
import { ReactComponent as Polygon } from '../../../res/svg/polygon.svg';
import { ReactComponent as Line } from '../../../res/svg/line.svg';
import { ReactComponent as SeleccionMultiple } from '../../../res/svg/seleccion-multiple.svg';
import {
  cancelAddingGeometry,
  openMapTutorial,
  selectAddingGeometry,
  selectAddingGeometryType,
  startAddingLine,
  startAddingPolygon,
} from '../../../store/slice/mapSlice';
import MapZoomButtons from '../../Atoms/MapZoomButtons/MapZoomButtons';
import MapLayersButton from '../../Atoms/MapLayersButton/MapLayersButton';
import { selectCurrentTask } from '../../../store/slice/taskSlice';
import { setAddingAOI } from '../../../store/slice/mapAOISlice';
import MapLocationButton from '../../Atoms/MapLocationButton/MapLocationButton';
import { useLocation } from 'react-router-dom';
import Task from '../../../models/Task';
import { stateTask } from '../../../utils/functions';
import { setAddingMultipleFeatures } from '../../../store/slice/mapLayersSlice';

interface Props {
  onZoomIn: () => any;
  onZoomOut: () => any;
}

/**
 * onlyViewButtonsMap
 * @param path
 * @description return a true if only the path is 'tareas/visualizar'
 * or the state of task is PENDIENTE, CERRADA o RECHAZADA
 * @returns
 */
 const onlyViewButtonsMap = (path:string, task?: Task) => {
  if(!path.search('/tareas/visualizar/')){
    return true;
  } else if (!path.search('/tareas/editar/') &&
    ( task?.estado === stateTask.PENDIENTE ||
      task?.estado === stateTask.CERRADA ||
      task?.estado === stateTask.RECHAZADA )
    ){
    return true
  }
  else return false;
}

function MapButtons({ onZoomIn, onZoomOut }: Props) {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const addingGeometry = useAppSelector(selectAddingGeometry);
  const addingAOI = useAppSelector(state => state.mapAOI.addingAOI);
  const addingGeometryType: 'punto' | 'linea' | 'poligono' = useAppSelector(
    selectAddingGeometryType,
  );
  const currentTask = useAppSelector(selectCurrentTask);
  const aoi = useAppSelector(state => state.mapAOI.aoi);
  const loadingAOI = useAppSelector(state => state.mapAOI.loading);
  const loadingMultipleSelection = useAppSelector(state => state.mapLayers.loadingMultipleSelection);
  let onlyView = onlyViewButtonsMap(location.pathname, currentTask);
  const layers = useAppSelector(state => state.mapLayers.layers);
  const cantAddAOI = layers.filter(l => l.type !== process.env.REACT_APP_AOI_GEOSERVER_LAYER_EXCLUDED).length === 0;
  const cantAddMultipleFeatures = layers.filter(l => l.type == process.env.REACT_APP_AOI_GEOSERVER_LAYER_EXCLUDED).length === 0;
  const addingMultipleFeatures = useAppSelector(state => state.mapLayers.addingMultipleFeatures);

  const onClickAddPoint = () => {
    dispatch(cancelAddingGeometry());
    if (!(addingGeometry && addingGeometryType === 'punto')) {
      dispatch(openMapTutorial());
    }
  };

  const onClickAOI = () => {
      dispatch(setAddingAOI(!addingAOI));
  };

  const onClickMultipleSelection = () => {
      dispatch(setAddingMultipleFeatures(!addingMultipleFeatures));
  };

  const onClickAddLine = () => {
    dispatch(cancelAddingGeometry());
    if (!(addingGeometry && addingGeometryType === 'linea')) {
      dispatch(startAddingLine());
    }
  };

  const onClickAddPolygon = () => {
    dispatch(cancelAddingGeometry());
    if (!(addingGeometry && addingGeometryType === 'poligono')) {
      dispatch(cancelAddingGeometry());
      dispatch(startAddingPolygon());
    }
  };

  return (
    <div className='org-map-buttons__container'>
      <div className='org-map-buttons__subcontainer'>
        <div className='org-map-buttons__top-button'>
          <MapLayersButton
            icon={Layers}
            tooltip='Visualizar y filtrar capas.'
          />
        </div>
        {currentTask?.id ? <div className='org-map-buttons__top-button'>
          {
            onlyView ? null :
              <MapButton
                icon={AddPoint}
                disabled={addingAOI || addingMultipleFeatures}
                active={addingGeometry && addingGeometryType === 'punto'}
                onClick={onClickAddPoint}
                tooltip='Agregar punto.'
              />
          }
        </div> : null}
        {addingGeometry ? (
          <div>
            <div className='org-map-buttons__top-button'>
              <MapButton
                onClick={onClickAddLine}
                active={addingGeometry && addingGeometryType === 'linea'}
                icon={Line}
                tooltip='Dibujar una línea.'
              />
            </div>
            <div className='org-map-buttons__top-button'>
              <MapButton
                onClick={onClickAddPolygon}
                active={addingGeometry && addingGeometryType === 'poligono'}
                icon={Polygon}
                tooltip='Dibujar un polígono.'
              />
            </div>
          </div>
        ) : null}
        {
          (!aoi || loadingAOI) && !onlyView ?
            <div className='org-map-buttons__top-button'>
              <MapButton
                icon={AOIIcon}
                active={addingAOI}
                loading={loadingAOI}
                onClick={onClickAOI}
                disabled={cantAddAOI  || addingGeometry || addingMultipleFeatures}
                tooltip='Agregar área de interés.'
                disabledTooltipDescription={'Necesitas activar las Capas Auxiliares o Productos Satelitales.'}
              />
            </div>
            : null
        }
        {
          onlyView ? null :
            <MapButton
              icon={SeleccionMultiple}
              active={addingMultipleFeatures}
              disabled={cantAddMultipleFeatures || addingGeometry || addingAOI}
              onClick={onClickMultipleSelection}
              loading={loadingMultipleSelection}
              disabledTooltipDescription={'Activa las Capas del Catastro Minero para utilizar esta herramienta.'}
              tooltip='Selección Múltiple.'
            />
        }
      </div>
      <div className='org-map-buttons__subcontainer'>
        <MapLocationButton />
        <div className='org-map-buttons__bottom-button'>
          <MapZoomButtons onZoomIn={onZoomIn} onZoomOut={onZoomOut} />
        </div>
      </div>
    </div>
  );
}

export default MapButtons;
